/**
 * PreflightPanel - the pre-flight checklist rows (SD-13), drawn in Navigate's
 * Pre-flight section (2026-10-05; a full-screen overlay until then). One row
 * per check with a PASS / WARN / FAIL / INFO glyph and a remedy hint on tap.
 * The section's header carries "Run again"; the Pre-flight card on Navigate's
 * home reads the same `lastResult` as a summary.
 */

import { For, Show, createSignal } from 'solid-js';
import { lastResult, running } from '../performance/preflightStore.ts';
import type { CheckStatus } from '../performance/preflight.ts';

const STATUS_GLYPH: Record<CheckStatus, string> = { pass: '✓', warn: '⚠', fail: '✕', info: 'ℹ' };

export function PreflightPanel() {
  // Which row's remedy is expanded (tap a row to toggle).
  const [openRow, setOpenRow] = createSignal<string | null>(null);
  const ranAt = () => {
    const r = lastResult();
    return r ? new Date(r.ranAtMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  };

  // The Navigate kit (DEC-255): a section of rows in a well. Tap a row with a
  // remedy to read what to do.
  return (
    <div class="nav-pane-body" role="region" aria-label="Pre-flight">
      <section class="nv-ss">
        <div class="nv-ss-h">
          <h2>Checks</h2>
          <Show when={ranAt()}><p>Checked at {ranAt()}. Tap a row for what to do.</p></Show>
        </div>
        <div class="nav-well preflight-rows" data-ran-at={lastResult()?.ranAtMs}>
          <Show
            when={lastResult()}
            fallback={<div class="nav-lr preflight-empty"><span class="nav-lr-main"><small>{running() ? 'Running the checks...' : 'Not checked yet.'}</small></span></div>}
          >
            {(r) => (
              <For each={r().rows}>
                {(row) => (
                  <button
                    type="button"
                    class="nav-lr nav-lr-button preflight-row"
                    data-status={row.status}
                    aria-expanded={row.remedy ? (openRow() === row.id ? 'true' : 'false') : undefined}
                    onClick={() => setOpenRow(openRow() === row.id ? null : row.id)}
                  >
                    <span class="nv-glyph preflight-row-glyph" data-status={row.status} aria-label={row.status}>{STATUS_GLYPH[row.status]}</span>
                    <span class="nav-lr-main">
                      <b class="preflight-row-label">{row.label}</b>
                      <small class="preflight-row-detail">{row.detail}</small>
                      <Show when={row.remedy && openRow() === row.id}>
                        <small class="preflight-row-remedy">{row.remedy}</small>
                      </Show>
                    </span>
                    <Show when={row.remedy}><span class="nav-lr-chev" aria-hidden="true">{openRow() === row.id ? '⌃' : '›'}</span></Show>
                  </button>
                )}
              </For>
            )}
          </Show>
        </div>
      </section>
    </div>
  );
}
